import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ClipboardText, Plus, Warning, ArrowLeft, FilePdf, Robot,
  CheckSquare, CheckCircle, Clock, ArrowClockwise, XCircle,
  ChatCircle, Eye, SortAscending, Buildings,
  CaretRight, Fire, PencilSimple, X, CalendarBlank,
  PaperPlaneRight, Users, Trash, Image
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import GanttPlanning from './GanttPlanning';
import ElementsDashboardCard from './ElementsDashboardCard';
import { CLIENT_HIDDEN_SUIVI_TABS, MOE_ROLES } from './projectRights';
import usePermissions from '../../hooks/usePermissions';
import ProjectPlanning, { dateLocalePourJour } from './ProjectPlanning';  // RP13 — calendrier des réunions (evenements)
import PlanifierReunionDialog from './PlanifierReunionDialog';  // A2 — formulaire unique « Planifier une réunion »
import HierarchyPicker from './plans/HierarchyPicker';
import CreateCRCDialog from './CreateCRCDialog';
import CreateTasksFromCRCDialog from './CreateTasksFromCRCDialog';
import SignatureManager from './SignatureManager';
import CRCMarkdownRenderer from './CRCMarkdownRenderer';
import ReunionSemaine from './ReunionSemaine';  // RP28-31 — écran « Réunion de la semaine »
// Phase D D.11 — Frontend megasprint : éditeur 16 sections + import externe + envoi enrichi
import ImportExternalCRCDialog from './crc/ImportExternalCRCDialog';
import SendCRCDialog from './crc/SendCRCDialog';
import CRCSectionEditor from './crc/CRCSectionEditor';
import { crcStatut, crcEstPublie, CRC_STATUT_LABELS } from './crc/crcStatut';
import { OBJETS, BOUTONS, FONCTIONS, libelleCompteRendu, titreCompteRendu } from '../../lib/libelles';
import { DownloadSimple, FileDoc, PaperPlaneTilt as PaperPlaneTiltD11 } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
  // Fluidité B2 (P3#20) — mêmes statuts que l'action : À faire → À vérifier → Levée
  EN_ATTENTE: { label: 'À faire', color: 'bg-[#FFF8E1] text-[#B8860B] border-[#B8860B]', icon: Clock },
  EN_COURS: { label: 'En cours', color: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]', icon: ArrowClockwise },
  A_VALIDER: { label: 'À vérifier', color: 'bg-[#F3E5F5] text-[#6A1B9A] border-[#6A1B9A]', icon: Eye },
  CLOTURE: { label: 'Levée', color: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]', icon: CheckCircle },
  REPORTE: { label: 'Reporté', color: 'bg-[#FFF3E0] text-[#E65100] border-[#E65100]', icon: ArrowClockwise },
  CONTESTE: { label: 'Contesté', color: 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]', icon: XCircle },
};

// CHA-07 — Texte officiel d'un CRC : même priorité que le backend
// (services/crc_texte_officiel.py) : rapport_ia > contenu_ia > pv_content.
const crcTexteOfficiel = (crc) => (crc && (crc.rapport_ia || crc.contenu_ia || crc.pv_content)) || '';

// CHA-02 — Liste des CRC : l'équipe MOE voit tous les CRC (y compris importés
// ou encore sans texte, signalés par un badge) ; les autres profils seulement
// les CRC publiés.
// CHA-11 — « publié » inclut les CRC diffusés (simple ou enrichi « ENVOYE »).
const isCrcVisible = (m, canCreate) => (canCreate ? true : crcEstPublie(m));

// RP17-19 / D2 / D14 — droits sur les comptes rendus, d'après le rôle PROJET renvoyé
// par /my-permissions (repli sur le rôle global tant qu'il n'est pas connu) :
//  - canCreate  : rédiger, modifier, « Soumettre à l'architecte » (tout membre MOE) ;
//  - canDiffuser : « Valider et diffuser » (droit « Peut diffuser un CR » : architecte,
//    chef de projet, ou collaborateur à qui le droit a été confié).
function useDroitsCR(chantierId, userRole) {
  const { perms, role_projet } = usePermissions(chantierId);
  const canCreate = role_projet ? MOE_ROLES.includes(role_projet) : ['admin', 'architect', 'collaborateur'].includes(userRole);
  const canDiffuser = canCreate && (role_projet ? !!perms?.can_diffuser_crc : userRole === 'admin');
  return { canCreate, canDiffuser };
}

// === POINT ROW COMPONENT ===
function PointRow({ point, userRole, chantierId, onStatusChange }) {
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [newStatut, setNewStatut] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [noteCloture, setNoteCloture] = useState('');
  const [nouvelleEcheance, setNouvelleEcheance] = useState('');
  const [planningProposal, setPlanningProposal] = useState(null); // {type:'cloture'|'report', tache}
  const [planningPct, setPlanningPct] = useState(100);
  const [planningDecalage, setPlanningDecalage] = useState(5);

  const config = STATUT_CONFIG[point.statut] || STATUT_CONFIG.EN_ATTENTE;
  const Icon = config.icon;
  const isAlerte = point.nb_crc_suspens >= 2;
  const canEntrepriseChange = userRole === 'entreprise' && (point.statut === 'EN_ATTENTE' || point.statut === 'EN_COURS');
  const canArchitectChange = ['admin', 'architect', 'collaborateur'].includes(userRole); // RP32
  const canChange = canEntrepriseChange || canArchitectChange;

  const getAvailableStatuts = () => {
    if (userRole === 'entreprise') {
      if (point.statut === 'EN_ATTENTE') return ['EN_COURS'];
      if (point.statut === 'EN_COURS') return ['A_VALIDER'];
      return [];
    }
    if (['admin', 'architect', 'collaborateur'].includes(userRole)) {
      return ['EN_ATTENTE', 'EN_COURS', 'A_VALIDER', 'CLOTURE', 'REPORTE', 'CONTESTE'].filter(s => s !== point.statut);
    }
    return [];
  };

  const handleSubmit = async () => {
    if (!newStatut) return;
    try {
      const { data } = await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/crc/${point.crc_id}/points/${point.id}/statut`,
        { statut: newStatut, commentaire, note_cloture: noteCloture, nouvelle_echeance: nouvelleEcheance || null },
        { withCredentials: true }
      );
      toast.success(`Point passé à "${STATUT_CONFIG[newStatut]?.label}"`);
      setChangeDialogOpen(false);
      // Check if linked to a planning task → propose update
      if (data.linked_tache) {
        if (newStatut === 'CLOTURE') {
          setPlanningProposal({ type: 'cloture', tache: data.linked_tache });
        } else if (newStatut === 'REPORTE') {
          setPlanningProposal({ type: 'report', tache: data.linked_tache });
        } else {
          setNewStatut(''); setCommentaire(''); setNoteCloture(''); setNouvelleEcheance('');
          onStatusChange();
        }
      } else {
        setNewStatut(''); setCommentaire(''); setNoteCloture(''); setNouvelleEcheance('');
        onStatusChange();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handlePlanningUpdate = async (action) => {
    if (!planningProposal) return;
    const tache = planningProposal.tache;
    try {
      if (action === 'mark100') {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${tache.tache_id}`, { avancement_pct: 100 }, { withCredentials: true });
        toast.success('Tâche marquée à 100%');
      } else if (action === 'custom_pct') {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${tache.tache_id}`, { avancement_pct: planningPct }, { withCredentials: true });
        toast.success(`Avancement mis à jour à ${planningPct}%`);
      } else if (action === 'decaler') {
        const origDate = new Date(tache.date_debut + 'T00:00:00');
        origDate.setDate(origDate.getDate() + planningDecalage);
        const newDate = origDate.toISOString().slice(0, 10);
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/planning/taches/${tache.tache_id}`, { date_debut: newDate }, { withCredentials: true });
        toast.success(`Tâche décalée de ${planningDecalage} jours`);
      }
    } catch { toast.error('Erreur mise à jour planning'); }
    setPlanningProposal(null);
    setNewStatut(''); setCommentaire(''); setNoteCloture(''); setNouvelleEcheance('');
    onStatusChange();
  };

  const dismissPlanningProposal = () => {
    setPlanningProposal(null);
    setNewStatut(''); setCommentaire(''); setNoteCloture(''); setNouvelleEcheance('');
    onStatusChange();
  };

  return (
    <div className={`border p-4 transition-colors ${isAlerte ? 'border-[#E65100] bg-[#FFF8F0]' : 'border-[#E0E0DB]'} ${point.statut === 'CLOTURE' ? 'opacity-50' : ''}`} data-testid={`point-row-${point.id}`}>
      <div className="flex items-start gap-3">
        {/* Left: status icon + origin badge */}
        <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
          <Icon size={18} className={config.color.split(' ')[1]} weight="fill" />
          {isAlerte && (
            <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-[#E65100] text-white text-[10px] font-bold" data-testid={`alerte-badge-${point.id}`} title={`En suspens depuis ${point.nb_crc_suspens} compte(s) rendu(s)`}>
              <Fire size={10} weight="fill" />
              {point.nb_crc_suspens}
            </div>
          )}
        </div>

        {/* Center: content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {point.is_reprise && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#F5F5F3] text-[#6B6B6B] border border-[#E0E0DB]" data-testid={`origin-badge-${point.id}`}>
                {point.crc_origine_titre} — Pt {point.point_numero}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${config.color}`}>
              {config.label}
            </span>
          </div>
          <p className={`text-sm font-medium ${point.statut === 'CLOTURE' ? 'line-through text-[#9A9A9A]' : 'text-[#2C2C2C]'}`}>
            {point.description}
          </p>
          <div className="flex items-center gap-4 mt-1 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1"><Buildings size={12} /> {point.entreprise}</span>
            <span>{point.lot}</span>
            {point.echeance && <span>Éch. {new Date(point.echeance).toLocaleDateString('fr-FR')}</span>}
          </div>
          {point.piece_id && (
            <div className="mt-1 text-[11px] text-[#6B4A24] italic flex items-center gap-1" data-testid={`point-loc-${point.id}`}>
              <span>📍</span>
              <span>{[point.batiment_nom, point.niveau, point.espace_nom, point.piece_nom].filter(Boolean).join(' · ')}</span>
            </div>
          )}
          {point.statut === 'A_VALIDER' && point.signale_traite_commentaire && (
            <div className="mt-2 p-2 bg-[#F3E5F5] border border-[#CE93D8] text-xs text-[#6A1B9A]">
              <strong>Signalé traité :</strong> {point.signale_traite_commentaire}
              {point.signale_traite_le && <span className="ml-2 text-[#9A9A9A]">({new Date(point.signale_traite_le).toLocaleDateString('fr-FR')})</span>}
            </div>
          )}
          {/* A2 (constat 23) — désaccord signalé par l'entreprise sur la page publique du CR */}
          {(point.desaccords || []).map((dz, k) => (
            <div key={`${dz.crc_id}-${k}`} className="mt-2 p-2 bg-[#FFEBEE] border border-[#EF9A9A] text-xs text-[#8B3A3A]" data-testid={`point-desaccord-${point.id}`}>
              <strong>Désaccord{dz.entreprise || dz.auteur ? ` de ${dz.entreprise || dz.auteur}` : ''}</strong>
              {dz.crc_numero ? ` (CR n° ${dz.crc_numero})` : ''} : {dz.commentaire}
              {dz.date && <span className="ml-2 text-[#9A9A9A]">({new Date(dz.date).toLocaleDateString('fr-FR')})</span>}
            </div>
          ))}
          {point.statut === 'CLOTURE' && point.note_cloture && (
            <div className="mt-2 p-2 bg-[#E8F5E9] border border-[#A5D6A7] text-xs text-[#2D6A4F]">
              Levé{point.cloture_le ? ` le ${new Date(point.cloture_le).toLocaleDateString('fr-FR')}` : ''} — {point.note_cloture}
            </div>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
        {canArchitectChange && (
          <LocaliserButton chantierId={chantierId} point={point} onDone={onStatusChange} />
        )}
        {canChange && point.statut !== 'CLOTURE' && (
          <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-none text-xs h-7" data-testid={`change-statut-${point.id}`}>
                Changer statut
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier le statut</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-[#F5F5F3] border border-[#E0E0DB] text-sm">
                  <p className="font-medium">{point.description}</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">{point.lot} — {point.entreprise}</p>
                </div>
                <div>
                  <Label>Nouveau statut</Label>
                  <Select value={newStatut} onValueChange={setNewStatut}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      {getAvailableStatuts().map(s => (
                        <SelectItem key={s} value={s}>{STATUT_CONFIG[s]?.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {newStatut === 'A_VALIDER' && userRole === 'entreprise' && (
                  <div>
                    <Label>Commentaire / Preuve (optionnel)</Label>
                    <Textarea value={commentaire} onChange={e => setCommentaire(e.target.value)} rows={2} className="rounded-none" placeholder="Décrivez la correction effectuée..." />
                  </div>
                )}
                {newStatut === 'CLOTURE' && (
                  <div>
                    <Label>Note de clôture</Label>
                    <Textarea value={noteCloture} onChange={e => setNoteCloture(e.target.value)} rows={2} className="rounded-none" placeholder="Conforme, validé sur site..." />
                  </div>
                )}
                {newStatut === 'REPORTE' && (
                  <div>
                    <Label>Nouvelle échéance</Label>
                    <Input type="date" value={nouvelleEcheance} onChange={e => setNouvelleEcheance(e.target.value)} className="rounded-none" />
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setChangeDialogOpen(false)} className="rounded-none">Annuler</Button>
                  <Button onClick={handleSubmit} disabled={!newStatut} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Confirmer</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Planning Proposal Dialog — Clôture */}
        {planningProposal?.type === 'cloture' && (
          <Dialog open={true} onOpenChange={dismissPlanningProposal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Mettre à jour le planning ?</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-[#6B6B6B]">
                  Ce point est lié à la tâche <strong className="text-[#2C2C2C]">"{planningProposal.tache.nom}"</strong> (actuellement à {planningProposal.tache.avancement_pct}%).
                  Souhaitez-vous mettre à jour l'avancement ?
                </p>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => handlePlanningUpdate('mark100')} className="bg-[#2D6A4F] hover:bg-[#3D8B6F] rounded-none gap-2 justify-start" data-testid="planning-mark-100">
                    <CheckCircle size={16} weight="bold" /> Marquer à 100%
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handlePlanningUpdate('custom_pct')} variant="outline" className="rounded-none gap-2 flex-shrink-0" data-testid="planning-custom-pct">
                      <PencilSimple size={16} /> Saisir un %
                    </Button>
                    <Input type="number" min={0} max={100} value={planningPct} onChange={e => setPlanningPct(parseInt(e.target.value) || 0)} className="rounded-none w-20" data-testid="planning-pct-input" />
                    <span className="text-sm text-[#6B6B6B]">%</span>
                  </div>
                  <Button onClick={dismissPlanningProposal} variant="ghost" className="rounded-none text-[#6B6B6B] justify-start" data-testid="planning-dismiss">
                    Non, ne pas modifier le planning
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Planning Proposal Dialog — Report */}
        {planningProposal?.type === 'report' && (
          <Dialog open={true} onOpenChange={dismissPlanningProposal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Décaler la tâche dans le planning ?</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-[#6B6B6B]">
                  Ce point reporté est lié à la tâche <strong className="text-[#2C2C2C]">"{planningProposal.tache.nom}"</strong> (début : {planningProposal.tache.date_debut}).
                  Souhaitez-vous décaler cette tâche ?
                </p>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-[#6B6B6B] flex-shrink-0">Décaler de</Label>
                  <Input type="number" min={1} max={90} value={planningDecalage} onChange={e => setPlanningDecalage(parseInt(e.target.value) || 1)} className="rounded-none w-20" data-testid="planning-decalage-input" />
                  <span className="text-sm text-[#6B6B6B]">jours</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => handlePlanningUpdate('decaler')} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2 justify-start" data-testid="planning-decaler-confirm">
                    <ArrowClockwise size={16} /> Oui, décaler de {planningDecalage} jours
                  </Button>
                  <Button onClick={dismissPlanningProposal} variant="ghost" className="rounded-none text-[#6B6B6B] justify-start" data-testid="planning-decaler-dismiss">
                    Non, ne pas modifier le planning
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>
    </div>
  );
}


function LocaliserButton({ chantierId, point, onDone }) {
  const [open, setOpen] = useState(false);
  const [loc, setLoc] = useState({
    batiment_id: point.batiment_id || null,
    niveau: point.niveau || null,
    espace_id: point.espace_id || null,
    piece_id: point.piece_id || null,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/crc/points/${point.id}`,
        { piece_id: loc.piece_id || null },
        { withCredentials: true }
      );
      toast.success(loc.piece_id ? 'Localisation enregistrée' : 'Localisation retirée');
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-none text-xs h-7 gap-1" data-testid={`localiser-${point.id}`}>
          <span>📍</span> {point.piece_id ? 'Modifier loc.' : 'Localiser'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Localisation du point</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-2 bg-[#F5F5F3] border border-[#E0E0DB] text-xs">
            <p className="font-medium">{point.description}</p>
            <p className="text-[#6B6B6B] mt-0.5">{point.lot} — {point.entreprise}</p>
          </div>
          <HierarchyPicker
            chantierId={chantierId}
            value={loc}
            onChange={setLoc}
            mode="full"
            label="Sélectionner la pièce concernée"
          />
          <div className="flex gap-2 justify-end pt-2">
            {point.piece_id && (
              <Button variant="ghost" size="sm" className="rounded-none text-[#8B3A3A]"
                onClick={() => { setLoc({ batiment_id: null, niveau: null, espace_id: null, piece_id: null }); }}
                data-testid={`localiser-clear-${point.id}`}>
                Retirer
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => setOpen(false)}>Annuler</Button>
            <Button size="sm" className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none" onClick={save} disabled={saving} data-testid={`localiser-save-${point.id}`}>
              {saving ? '...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// === CRC DETAIL VIEW ===
function CRCDetailView({ crc, chantierId, userRole, onBack }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addPointOpen, setAddPointOpen] = useState(false);
  const [newPoint, setNewPoint] = useState({ lot: '', description: '', entreprise: '', echeance: '' });
  const [editingNotes, setEditingNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState(crc.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [planningAlertes, setPlanningAlertes] = useState([]);
  const [showPlanningAlert, setShowPlanningAlert] = useState(true);
  const [planningTaches, setPlanningTaches] = useState([]);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);

  const { canCreate, canDiffuser } = useDroitsCR(chantierId, userRole);

  useEffect(() => { fetchPoints(); fetchPlanningData(); }, [crc.id]);

  const fetchPlanningData = async () => {
    try {
      const [alertRes, planRes] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/alertes-crc`, { withCredentials: true }).catch(() => ({ data: { alertes: [] } })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/planning`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPlanningAlertes(alertRes.data?.alertes || []);
      setPlanningTaches((planRes.data?.taches || []).filter(t => t.type === 'TACHE'));
    } catch { /* ignore */ }
  };

  const fetchPoints = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/points`, { withCredentials: true });
      setPoints(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAddPoint = async (e) => {
    e.preventDefault();
    try {
      const { tache_gantt_id, _loc, ...pointData } = newPoint;
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/points`, pointData, { withCredentials: true });
      // Create CRC-Planning link if task selected
      if (tache_gantt_id && tache_gantt_id !== 'none' && data?.id) {
        await axios.post(`${API_URL}/api/chantiers/${chantierId}/planning/lien-crc`, { point_crc_id: data.id, tache_gantt_id }, { withCredentials: true }).catch(() => {});
      }
      toast.success('Point ajouté');
      setAddPointOpen(false);
      setNewPoint({ lot: '', description: '', entreprise: '', echeance: '', piece_id: null, _loc: null });
      fetchPoints();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // CHA-13 — les notes sont réellement enregistrées (PATCH /crc/{id}, champ `notes`)
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}`,
        { notes: editingNotes },
        { withCredentials: true },
      );
      setNotes(data?.notes ?? editingNotes);
      setIsEditingNotes(false);
      toast.success('Notes enregistrées');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Les notes n'ont pas pu être enregistrées");
    } finally {
      setSavingNotes(false);
    }
  };

  const reprises = points.filter(p => p.is_reprise);
  const nouveaux = points.filter(p => !p.is_reprise);

  return (
    <div data-testid="crc-detail-view">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" className="rounded-none gap-2 text-[#6B6B6B] hover:text-[#2C2C2C]" onClick={() => onBack(notes !== (crc.notes || ""))} data-testid="crc-back-button">
          <ArrowLeft size={18} /> Retour
        </Button>
      </div>

      {/* Planning Alert */}
      {canCreate && showPlanningAlert && planningAlertes.length > 0 && (
        <div className="bg-[#FFF3CD] border border-[#D4A84B] px-4 py-3 flex items-start gap-3 mb-4" data-testid="planning-alert-crc">
          <Warning size={20} className="text-[#B8860B] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[#2C2C2C]">
              {planningAlertes.length} tâche{planningAlertes.length > 1 ? 's sont' : ' est'} en retard depuis votre dernier compte rendu
            </p>
            <div className="mt-1 space-y-0.5">
              {planningAlertes.slice(0, 3).map(a => (
                <p key={a.tache_id} className="text-xs text-[#6B6B6B]">
                  {a.nom} ({a.entreprise_nom}) — <span className="text-[#C75B39] font-medium">+{a.retard_jours}j</span>
                </p>
              ))}
              {planningAlertes.length > 3 && <p className="text-xs text-[#6B6B6B]">et {planningAlertes.length - 3} autre{planningAlertes.length - 3 > 1 ? 's' : ''}...</p>}
            </div>
          </div>
          <Button variant="ghost" size="sm" className="rounded-none text-xs h-7" onClick={() => setShowPlanningAlert(false)}>
            <X size={14} />
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content - 3 cols */}
        <div className="lg:col-span-3 space-y-6">
          {/* Header */}
          <div className="card-kroquis">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h2 className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>{titreCompteRendu(crc.title)}</h2>
                <p className="text-sm text-[#6B6B6B] mt-1">
                  {new Date(crc.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {crc.location && ` — ${crc.location}`}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 bg-[#F5F5F3] text-[#6B6B6B] font-medium">{points.length} points</span>
                {reprises.length > 0 && <span className="px-2 py-1 bg-[#FFF3E0] text-[#E65100] font-medium">{reprises.length} en suspens</span>}
              </div>
            </div>
            {/* Notes */}
            {(notes || isEditingNotes || canCreate) && (
              <div className="mt-4 pt-4 border-t border-[#E0E0DB]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">Notes / Ordre du jour</h4>
                  {canCreate && !isEditingNotes && (
                    <Button variant="ghost" size="sm" className="rounded-none gap-1 h-6 text-xs" onClick={() => { setIsEditingNotes(true); setEditingNotes(notes || ''); }} data-testid="crc-notes-edit">
                      <PencilSimple size={12} /> {notes ? 'Modifier' : 'Ajouter'}
                    </Button>
                  )}
                </div>
                {isEditingNotes ? (
                  <div className="space-y-2">
                    <Textarea value={editingNotes} onChange={e => setEditingNotes(e.target.value)} rows={4} className="rounded-none" data-testid="crc-notes-textarea" />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="rounded-none" onClick={() => setIsEditingNotes(false)} disabled={savingNotes}>Annuler</Button>
                      <Button size="sm" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" onClick={handleSaveNotes} disabled={savingNotes} data-testid="crc-notes-save">
                        {savingNotes ? 'Enregistrement…' : 'Enregistrer'}
                      </Button>
                    </div>
                  </div>
                ) : notes ? (
                  <p className="text-sm text-[#4A4A4A] whitespace-pre-wrap p-3 bg-[#F5F5F3]">{notes}</p>
                ) : (
                  <p className="text-xs text-[#9A9A9A] italic">Aucune note.</p>
                )}
              </div>
            )}
          </div>

          {/* CHA-02 — CR importé (PDF externe) : données extraites par l'OCR */}
          {crc.imported_externe && (
            <div className="card-kroquis border-l-4 border-l-purple-400" data-testid="crc-imported-info">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">CR importé</h3>
              <p className="text-xs text-[#6B6B6B]">
                Fichier d'origine : {crc.imported_from_filename || '—'}
              </p>
              {crc.ocr_error && (
                <p className="text-xs text-[#8B3A3A] mt-1">
                  La lecture automatique du PDF a échoué ({crc.ocr_error}) : complétez les points à la main.
                </p>
              )}
              {crc.objet && <p className="text-sm text-[#2C2C2C] mt-2"><strong>Objet :</strong> {crc.objet}</p>}
              {crc.synthese_semaine && (
                <p className="text-sm text-[#4A4A4A] whitespace-pre-wrap mt-2">{crc.synthese_semaine}</p>
              )}
            </div>
          )}

          {/* CRC Content (IA-generated or imported) with workflow */}
          {(crcTexteOfficiel(crc) || crc.imported_externe) && (
            <CRCContentEditor crc={crc} chantierId={chantierId} canEdit={canCreate} canDiffuser={canDiffuser} onUpdated={() => onBack(true)} />
          )}

          {/* Signatures électroniques (seulement pour CRC publiés/validés) */}
          {['valide', 'publie', 'diffuse'].includes(crcStatut(crc)) && (
            <div className="mt-4">
              <SignatureManager
                chantierId={chantierId}
                documentType="crc"
                documentId={crc.id}
                documentTitle={`${libelleCompteRendu(crc.numero || '?')} — ${titreCompteRendu(crc.title)}`}
              />
            </div>
          )}

          {/* Section 1: Points en suspens */}
          {reprises.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-[#E65100]" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#E65100]">Points en suspens ({reprises.length})</h3>
              </div>
              <div className="space-y-2">
                {loading ? [...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />) :
                  reprises.map(p => <PointRow key={p.id} point={p} userRole={userRole} chantierId={chantierId} onStatusChange={fetchPoints} />)
                }
              </div>
            </div>
          )}

          {/* Section 2: Nouveaux points */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-[#1E3A5F]" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F]">Nouveaux points ({nouveaux.length})</h3>
              </div>
              {canCreate && (
                <Dialog open={addPointOpen} onOpenChange={setAddPointOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-1 text-xs h-8" data-testid="add-point-button">
                      <Plus size={14} weight="bold" /> Nouveau point
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Nouvelle action du compte rendu</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddPoint} className="space-y-4">
                      <div>
                        <Label>Description du point</Label>
                        <Textarea value={newPoint.description} onChange={e => setNewPoint({...newPoint, description: e.target.value})} required rows={3} className="rounded-none" placeholder="Décrivez le problème relevé..." />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Lot</Label>
                          <Input value={newPoint.lot} onChange={e => setNewPoint({...newPoint, lot: e.target.value})} required className="rounded-none" placeholder="Plomberie" />
                        </div>
                        <div>
                          <Label>Entreprise responsable</Label>
                          <Input value={newPoint.entreprise} onChange={e => setNewPoint({...newPoint, entreprise: e.target.value})} required className="rounded-none" placeholder="Plomberie Martin" />
                        </div>
                      </div>
                      <div>
                        <Label>Échéance</Label>
                        <Input type="date" value={newPoint.echeance} onChange={e => setNewPoint({...newPoint, echeance: e.target.value})} className="rounded-none" />
                      </div>
                      <div>
                        <Label>Localisation (facultatif)</Label>
                        <HierarchyPicker
                          chantierId={chantierId}
                          value={newPoint._loc || null}
                          onChange={loc => setNewPoint({...newPoint, _loc: loc, piece_id: loc?.piece_id || null})}
                          mode="full"
                          compact
                          label=""
                        />
                      </div>
                      {planningTaches.length > 0 && (
                        <div>
                          <Label>Tâche concernée (planning)</Label>
                          <Select value={newPoint.tache_gantt_id || ''} onValueChange={v => setNewPoint({...newPoint, tache_gantt_id: v})}>
                            <SelectTrigger className="rounded-none"><SelectValue placeholder="Optionnel — Lier à une tâche du planning" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Aucune</SelectItem>
                              {planningTaches.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.lot ? `${t.lot} → ` : ''}{t.nom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setAddPointOpen(false)} className="rounded-none">Annuler</Button>
                        <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Ajouter</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div className="space-y-2">
              {loading ? [...Array(2)].map((_, i) => <Skeleton key={i} className="h-20" />) :
                nouveaux.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-[#E0E0DB]">
                    <p className="text-sm text-[#6B6B6B]">Aucun nouveau point pour cette réunion</p>
                  </div>
                ) : nouveaux.map(p => <PointRow key={p.id} point={p} userRole={userRole} chantierId={chantierId} onStatusChange={fetchPoints} />)
              }
            </div>
          </div>

          {/* PV Content est maintenant rendu proprement par CRCMarkdownRenderer dans CRCContentEditor. Plus besoin d'un bloc brut. */}
        </div>

        {/* Sidebar - 1 col */}
        <div className="space-y-4">
          <div className="card-kroquis">
            <h4 className="text-sm font-semibold text-[#2C2C2C] mb-3">Actions rapides</h4>
            <div className="space-y-2">
              {/* CHA-13 — boutons morts retirés : « Générer le PV » (placeholder qui écrivait
                  un faux PV), « Télécharger le PV » et « Joindre un document » (sans action).
                  Les exports Word / annexe photos restent dans le bloc « Compte rendu ». */}
              {/* M3 Assistant — Créer les tâches associées au CRC */}
              {canCreate && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 rounded-none text-xs border-[#C4773B] text-[#C4773B] hover:bg-[#C4773B] hover:text-white"
                  onClick={() => setTasksDialogOpen(true)}
                  data-testid="generate-tasks-from-crc"
                >
                  <Robot size={14} /> Créer les actions associées
                </Button>
              )}
            </div>
          </div>
          <CreateTasksFromCRCDialog
            open={tasksDialogOpen}
            onClose={() => setTasksDialogOpen(false)}
            chantierId={chantierId}
            crc={crc}
            onCreated={(count) => {
              setTasksDialogOpen(false);
              if (typeof window !== 'undefined') {
                import('sonner').then(({ toast }) => toast.success(`${count} action${count > 1 ? 's' : ''} créée${count > 1 ? 's' : ''}`));
              }
            }}
          />
          {/* Stats */}
          <div className="card-kroquis">
            <h4 className="text-sm font-semibold text-[#2C2C2C] mb-3">Résumé</h4>
            <div className="space-y-2 text-xs">
              {Object.entries(STATUT_CONFIG).map(([key, cfg]) => {
                const count = points.filter(p => p.statut === key).length;
                if (count === 0) return null;
                return (
                  <div key={key} className="flex items-center justify-between">
                    <span className={`px-1.5 py-0.5 border ${cfg.color}`}>{cfg.label}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// === MAIN SUIVI COMPONENT ===
export default function ProjectSuivi({ chantierId, userRole, tabRequest, onSeeAllElements, navigationExterne = false, onSubTabChange }) {
  // Fluidité B2 — `navigationExterne` : l'onglet « Chantier » du chantier affiche déjà
  // Réunions · Comptes rendus · Actions · … ; ce composant n'affiche alors ni sa propre
  // barre de sous-onglets, ni un second bouton « Actions », ni le tableau des actions
  // (qui est dans Chantier › Actions). `onSubTabChange` signale un changement de vue
  // fait ici (ex. « Comptes rendus » après la création d'un CR depuis une réunion).
  // CHA-15 — sous-onglet piloté : `tabRequest` ({ tab, at }) permet au badge
  // « nouveautés depuis le dernier CRC » d'ouvrir « Comptes Rendus », même si
  // l'onglet Suivi est déjà affiché ; le sous-onglet est conservé au retour d'un CRC.
  // RP01 — pour le client (MOA) : pas de carte des éléments (fiche 06 §3).
  // RP14 (ARC-R5) — les deux anciens sous-onglets d'actions (points-ouverts et
  // priorites) sont retirés : une seule liste « Actions » (onglet
  // du chantier, filtres par compte rendu / lot / entreprise). Un ancien lien profond
  // vers ces sous-onglets retombe sur le planning.
  const isClient = userRole === 'client';
  const allowSubTab = (t) => (t && !CLIENT_HIDDEN_SUIVI_TABS.includes(t) ? t : 'planning');
  const [activeSubTab, setActiveSubTab] = useState(allowSubTab(tabRequest?.tab));
  const [meetings, setMeetings] = useState([]);
  const [docsImportes, setDocsImportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crcDialogOpen, setCrcDialogOpen] = useState(false);
  const [selectedCRC, setSelectedCRC] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  // Phase D D.11 frontend megasprint
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editorCrcId, setEditorCrcId] = useState(null);
  const [sendingCrcId, setSendingCrcId] = useState(null);

  const isAMO = userRole === 'amo';
  const { canCreate, canDiffuser } = useDroitsCR(chantierId, userRole);

  useEffect(() => { fetchMeetings(); fetchDocsImportes(); }, [chantierId]);

  useEffect(() => {
    if (!tabRequest?.tab) return;
    setActiveSubTab(allowSubTab(tabRequest.tab));
    setSelectedCRC(null);
  }, [tabRequest]);

  useEffect(() => {
    if (onSubTabChange) onSubTabChange(activeSubTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  const fetchDocsImportes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/${chantierId}`, { withCredentials: true });
      setDocsImportes((data || []).filter(d => d.type === 'pv' || d.type === 'comptes_rendus'));
    } catch { /* */ }
  };

  const fetchMeetings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/meetings/${chantierId}`, { withCredentials: true });
      setMeetings(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Detail view
  if (selectedCRC) {
    return <CRCDetailView crc={selectedCRC} chantierId={chantierId} userRole={userRole} onBack={(reload) => { setSelectedCRC(null); if (reload) fetchMeetings(); }} />;
  }

  // Phase D D.11 — Modals montees au top-level pour eviter conflits z-index avec Tabs
  const d11Modals = (
    <>
      {showImportDialog && (
        <ImportExternalCRCDialog
          chantierId={chantierId}
          onClose={() => setShowImportDialog(false)}
          onSuccess={(result) => {
            setShowImportDialog(false);
            fetchMeetings();
            toast.success(`CR n°${result.numero || '?'} importé`);
          }}
        />
      )}
      {editorCrcId && (
        <CRCSectionEditor
          chantierId={chantierId}
          crcId={editorCrcId}
          onClose={() => {
            setEditorCrcId(null);
            fetchMeetings();
          }}
        />
      )}
      {sendingCrcId && (
        <SendCRCDialog
          chantierId={chantierId}
          crc={meetings.find((m) => (m.id || m._id) === sendingCrcId) || { id: sendingCrcId }}
          onClose={() => setSendingCrcId(null)}
          onSuccess={(res) => {
            setSendingCrcId(null);
            fetchMeetings();
            const pj = (res?.pieces_jointes || []).length;
            toast.success(`Envoyé à ${res?.sent_count || 0} destinataire(s)${pj ? ` avec ${pj} pièce(s) jointe(s)` : ''}`);
            if (res?.failed_count) {
              toast.error(`Échec pour ${res.failed_count} destinataire(s) : ${(res.failed || []).map((f) => f.email).join(', ')}`);
            }
          }}
        />
      )}
    </>
  );

  return (
    <div data-testid="project-suivi">
      {d11Modals}
      {!isClient && !navigationExterne && <ElementsDashboardCard chantierId={chantierId} onSeeAll={onSeeAllElements} />}
      <Tabs value={activeSubTab} onValueChange={(t) => setActiveSubTab(allowSubTab(t))} className="w-full">
        <div className={navigationExterne && !(canCreate && activeSubTab === 'crc') ? 'hidden' : 'flex items-center justify-between mb-6'}>
          {navigationExterne ? <span /> : (
          <TabsList className="bg-[#F5F5F3] rounded-none p-1 h-auto">
            <TabsTrigger value="planning" className="rounded-none data-[state=active]:bg-white gap-2" data-testid="planning-tab">
              <ClipboardText size={16} /> Planning
            </TabsTrigger>
            <TabsTrigger value="reunions" className="rounded-none data-[state=active]:bg-white gap-2" data-testid="reunions-tab">
              <CalendarBlank size={16} /> Réunions
            </TabsTrigger>
            <TabsTrigger value="crc" className="rounded-none data-[state=active]:bg-white gap-2">
              <ClipboardText size={16} /> {OBJETS.comptesRendus}
            </TabsTrigger>
          </TabsList>
          )}

          {/* RP14 — les actions (des CR et du chantier) vivent dans une liste unique */}
          {!isClient && onSeeAllElements && !navigationExterne && (
            <Button
              variant="outline"
              onClick={onSeeAllElements}
              className="rounded-none gap-2 mr-auto ml-3"
              data-testid="suivi-lien-actions"
            >
              <Warning size={16} /> {OBJETS.actions}
            </Button>
          )}

          {/* A2 (constat 5) — les boutons des comptes rendus ne s'affichent que dans l'onglet
              Comptes rendus ; dans Réunions, le CR est proposé à partir de la réunion. */}
          {canCreate && activeSubTab === 'crc' && (
            <div className="flex items-center gap-2">
              {/* Phase D D.11 — Import CRC externe (Claude Vision PDF) */}
              <Button
                onClick={() => setShowImportDialog(true)}
                variant="outline"
                className="rounded-none gap-2"
                data-testid="import-external-crc-button"
              >
                <DownloadSimple size={18} /> Importer CR antérieurs
              </Button>
              <Button onClick={() => setCrcDialogOpen(true)} className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2" data-testid="new-crc-button">
                <Plus size={18} weight="bold" /> {BOUTONS.nouveauCompteRendu}
              </Button>
              <CreateCRCDialog
                chantierId={chantierId}
                open={crcDialogOpen}
                onClose={() => setCrcDialogOpen(false)}
                onRequestImport={() => setShowImportDialog(true)}
                onCreated={(crc) => {
                  setCrcDialogOpen(false);
                  fetchMeetings();
                  fetchDocsImportes();
                  if (crc && crc.id) setSelectedCRC(crc);
                }}
              />
            </div>
          )}
        </div>

        {/* CRC Tab */}
        <TabsContent value="crc">
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
          ) : (
            <div className="space-y-3">
              {meetings.filter(m => isCrcVisible(m, canCreate)).map(meeting => {
                // CHA-11 — état canonique (ENVOYE → Diffusé) au lieu d'un repli « Brouillon »
                const statut = crcStatut(meeting);
                const statutCfg = {
                  brouillon: { label: CRC_STATUT_LABELS.brouillon, cls: 'bg-[#FFF8E1] text-[#B8860B]' },
                  valide: { label: CRC_STATUT_LABELS.valide, cls: 'bg-[#E3F2FD] text-[#1E3A5F]' },
                  publie: { label: CRC_STATUT_LABELS.publie, cls: 'bg-[#E8F5E9] text-[#2D6A4F]' },
                  diffuse: { label: CRC_STATUT_LABELS.diffuse, cls: 'bg-[#2D6A4F] text-white' },
                  signe: { label: CRC_STATUT_LABELS.signe, cls: 'bg-[#1E3A5F] text-white' },
                }[statut] || { label: CRC_STATUT_LABELS.brouillon, cls: 'bg-[#FFF8E1] text-[#B8860B]' };
                const period = (meeting.date_debut_periode && meeting.date_fin_periode)
                  ? `${new Date(meeting.date_debut_periode).toLocaleDateString('fr-FR')} → ${new Date(meeting.date_fin_periode).toLocaleDateString('fr-FR')}`
                  : null;
                return (
                  <div key={meeting.id} className="card-kroquis hover:border-[#2C2C2C] cursor-pointer transition-colors group" onClick={() => setSelectedCRC(meeting)} data-testid={`crc-item-${meeting.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-medium text-[#2C2C2C]">{titreCompteRendu(meeting.title)}</h3>
                          <span className={`text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide ${statutCfg.cls}`}>{statutCfg.label}</span>
                          {meeting.source === 'claude-sonnet' && <span className="text-[10px] text-[#C4773B] flex items-center gap-1"><Robot size={10} /> IA</span>}
                          {/* Phase D D.11 — Badges enrichis */}
                          {meeting.imported_externe && <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded" data-testid={`crc-badge-importe-${meeting.id}`}>📥 Importé</span>}
                          {!meeting.imported_externe && !crcTexteOfficiel(meeting) && (
                            <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded" title="Aucun texte rédigé pour ce compte rendu" data-testid={`crc-badge-vide-${meeting.id}`}>Vide</span>
                          )}
                          {meeting.pdf_final_filename && <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded">✅ PDF</span>}
                        </div>
                        <p className="text-sm text-[#6B6B6B]">
                          {period ? (
                            <>Période : {period} · </>
                          ) : null}
                          {/* A2 / T5 — date de la réunion liée quand elle existe */}
                          {meeting.date_reunion ? 'Réunion du' : 'Créé le'} {new Date(meeting.date_reunion || meeting.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          {meeting.location && ` — ${meeting.location}`}
                        </p>
                        {statut === 'valide' && meeting.soumis_relecture_par_nom && (
                          <p className="text-xs text-[#1E3A5F] mt-0.5" data-testid={`crc-soumis-${meeting.id}`}>
                            Soumis à l'architecte par {meeting.soumis_relecture_par_nom}
                          </p>
                        )}
                      </div>
                      {/* Phase D D.11 — Actions enrichies par ligne */}
                      {canCreate && (
                        <div className="flex items-center gap-1 mr-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setEditorCrcId(meeting.id || meeting._id)}
                            className="p-1.5 text-[#6B6B6B] hover:text-[#1E3A5F] hover:bg-gray-100 rounded"
                            title={BOUTONS.modifierCompteRendu}
                            data-testid={`crc-edit-${meeting.id}`}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const res = await axios.get(
                                  `${API_URL}/api/chantiers/${chantierId}/crc/${meeting.id || meeting._id}/export-docx-v2`,
                                  { responseType: 'blob', withCredentials: true },
                                );
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `CR_n${meeting.numero || '?'}_v2.docx`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (e) {
                                toast.error(`Téléchargement Word : ${e.response?.data?.detail || e.message}`);
                              }
                            }}
                            className="p-1.5 text-[#6B6B6B] hover:text-[#1E3A5F] hover:bg-gray-100 rounded"
                            title={BOUTONS.telechargerWord}
                            data-testid={`crc-export-v2-${meeting.id}`}
                          >
                            <FileDoc size={16} />
                          </button>
                          {/* RP18 — « Valider et diffuser » : droit « Peut diffuser un CR » seulement */}
                          {canDiffuser && (
                          <button
                            onClick={() => setSendingCrcId(meeting.id || meeting._id)}
                            className="p-1.5 text-[#6B6B6B] hover:text-green-600 hover:bg-gray-100 rounded"
                            title={BOUTONS.validerEtDiffuser}
                            data-testid={`crc-send-${meeting.id}`}
                          >
                            <PaperPlaneTiltD11 size={16} />
                          </button>
                          )}
                        </div>
                      )}
                      <CaretRight size={18} className="text-[#E0E0DB] group-hover:text-[#2C2C2C] transition-colors" />
                    </div>
                  </div>
                );
              })}
              {meetings.filter(m => isCrcVisible(m, canCreate)).length === 0 && (
                <div className="card-kroquis text-center py-16 border-2 border-dashed border-[#E0E0DB]">
                  <ClipboardText size={48} className="mx-auto text-[#E0E0DB] mb-4" />
                  <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">
                    {canCreate ? 'Aucun compte rendu pour ce chantier' : 'Aucun compte rendu publié pour le moment'}
                  </h3>
                  {canCreate && (
                    <>
                      <p className="text-sm text-[#6B6B6B] max-w-md mx-auto mb-4">
                        Démarrez le suivi en créant votre premier compte rendu (CR) ou en important des CR antérieurs (PDF).
                      </p>
                      <div className="flex items-center justify-center gap-3 flex-wrap">
                        <Button
                          onClick={() => setCrcDialogOpen(true)}
                          className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2"
                          data-testid="empty-state-create-crc"
                        >
                          <Plus size={18} weight="bold" /> Créer le premier compte rendu
                        </Button>
                        <Button
                          onClick={() => setShowImportDialog(true)}
                          variant="outline"
                          className="rounded-none gap-2"
                          data-testid="empty-state-import-crc"
                        >
                          <DownloadSimple size={18} /> Importer CR antérieurs
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {/* CR Importés */}
          {docsImportes.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Comptes rendus importés ({docsImportes.length})</h3>
              <div className="space-y-2">
                {docsImportes.map(doc => (
                  <div key={doc.id} className="card-kroquis hover:border-[#C4773B] cursor-pointer transition-colors flex items-center gap-3" onClick={() => doc.stored_name ? setPreviewDoc(doc) : null} data-testid={`cr-import-${doc.id}`}>
                    <FilePdf size={20} className="text-[#C75B39] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#2C2C2C]">{doc.name}</p>
                      <p className="text-xs text-[#6B6B6B]">{doc.uploaded_by_name && `${doc.uploaded_by_name} — `}{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : ''}</p>
                    </div>
                    {doc.stored_name && <Eye size={16} className="text-[#9A9A9A]" />}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Preview modal */}
          {previewDoc && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setPreviewDoc(null)}>
              <div className="bg-white flex flex-col overflow-hidden" style={{ width: '90vw', height: '90vh', maxWidth: 1200, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#E0E0DB]">
                  <h3 className="text-base font-semibold text-[#2C2C2C] truncate flex-1 mr-4">{previewDoc.name}</h3>
                  <button onClick={() => setPreviewDoc(null)} className="p-1 hover:bg-[#F5F5F3]"><X size={18} /></button>
                </div>
                <div className="flex-1 overflow-auto bg-[#F5F5F3] p-4">
                  <iframe src={`${API_URL}/api/chantiers/${chantierId}/files/${previewDoc.stored_name}`} title={previewDoc.name} style={{ width: '100%', height: '100%', border: 'none' }} />
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Planning Gantt Tab */}
        <TabsContent value="planning">
          <GanttPlanning chantierId={chantierId} userRole={userRole} />
        </TabsContent>

        {/* Réunions Tab */}
        <TabsContent value="reunions">
          <ReunionsSection
            chantierId={chantierId}
            userRole={userRole}
            onCrCree={(crc) => { fetchMeetings(); setActiveSubTab('crc'); if (crc && crc.id) setSelectedCRC(crc); }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// A2 (vague « Fluidité », constats 3, 5, 25) — Suivi › Réunions :
//  - un seul bouton de création, « Planifier une réunion » (formulaire commun
//    PlanifierReunionDialog, pré-rempli avec le jour choisi dans le calendrier) ;
//  - « Réunion de la semaine » ouvre la réunion du jour ou la prochaine planifiée ;
//  - chaque réunion affiche « Convoquée le … » ; « Renvoyer » demande confirmation ;
//  - le compte rendu est proposé à partir de la réunion (« Rédiger le compte rendu »).
const dateCourteFr = (iso) => {
  if (!iso) return '';
  const s = String(iso);
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  return Number.isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

function ReunionsSection({ chantierId, userRole, onCrCree }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planifier, setPlanifier] = useState(null);  // null = fermé, sinon date initiale ('' possible)
  const [jourChoisi, setJourChoisi] = useState('');
  const [selectedEvt, setSelectedEvt] = useState(null);
  const [reunionSemaine, setReunionSemaine] = useState(null);  // RP28-31 : { evenementId }
  const [crPourReunion, setCrPourReunion] = useState(null);    // id de la réunion

  // POST /chantiers/{id}/evenements : rôle projet MOE (même règle que les CR).
  const { canCreate } = useDroitsCR(chantierId, userRole);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEvents(); }, [chantierId]);

  const loadEvents = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/evenements`, { withCredentials: true });
      setEvents(Array.isArray(data) ? data : []);
    } catch { /* */ } finally { setLoading(false); }
  };

  // Le jour choisi dans le calendrier pré-remplit le formulaire s'il n'est pas passé.
  const ouvrirPlanification = () => {
    const aujourdhui = dateLocalePourJour(new Date()).slice(0, 10);
    setPlanifier(jourChoisi && jourChoisi.slice(0, 10) >= aujourdhui ? jourChoisi : '');
  };

  const sendConvocations = async (evt, renvoyer = false) => {
    if (renvoyer) {
      const n = (evt.invites || []).filter((i) => i.email).length;
      if (!window.confirm(`Renvoyer la convocation à ${n} participant${n > 1 ? 's' : ''} ? Ils recevront un nouvel e-mail.`)) return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/evenements/${evt.id}/convoquer`, renvoyer ? { renvoyer: true } : {}, { withCredentials: true });
      if (data.deja_convoquee) toast.info(data.message); else toast.success(data.message);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'La convocation n\'a pas pu être envoyée.'); }
  };

  const sendRappel = async (eid) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/evenements/${eid}/rappel`, {}, { withCredentials: true });
      toast.success(data.message);
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Le rappel n\'a pas pu être envoyé.'); }
  };

  const deleteEvent = async (eid) => {
    if (!window.confirm('Supprimer cette réunion ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/evenements/${eid}`, { withCredentials: true });
      toast.success('Réunion supprimée');
      if (selectedEvt?.id === eid) setSelectedEvt(null);
      loadEvents();
    } catch (e) { toast.error('Erreur'); }
  };

  const debutJour = new Date(); debutJour.setHours(0, 0, 0, 0);
  const upcoming = events.filter(e => new Date(e.date_debut) >= debutJour).sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
  const past = events.filter(e => new Date(e.date_debut) < debutJour).sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut)).slice(0, 5);

  const EtatConvocation = ({ evt }) => (evt.convocation_envoyee ? (
    <span className="text-[10px] px-1.5 py-0.5 bg-[#E8F5E9] text-[#2D6A4F] font-semibold" data-testid={`reunion-convoquee-${evt.id}`}>
      Convoquée{evt.convocation_envoyee_at ? ` le ${dateCourteFr(evt.convocation_envoyee_at)}` : ''}
    </span>
  ) : (
    <span className="text-[10px] px-1.5 py-0.5 bg-[#FFF8E1] text-[#B8860B] font-semibold">Pas encore convoquée</span>
  ));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-base font-semibold" style={{ fontFamily: 'DM Serif Display, serif' }}>Réunions de chantier</h3>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            <Button className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-2 min-h-[44px]" onClick={ouvrirPlanification} data-testid="new-reunion-btn">
              <Plus size={16} /> Planifier une réunion
            </Button>
            {/* RP28-31 (ARC-A1) — présence → actions → CR → diffusion sur un seul écran ;
                A2 (constat 25) : ouvre la réunion du jour ou la prochaine planifiée */}
            <Button variant="outline" className="rounded-none gap-2 min-h-[44px]" onClick={() => setReunionSemaine({ evenementId: null })} data-testid="reunion-semaine-btn">
              <Users size={16} /> {FONCTIONS.reunionSemaine}
            </Button>
          </div>
        )}
      </div>
      {reunionSemaine && (
        <ReunionSemaine chantierId={chantierId} evenementId={reunionSemaine.evenementId} onClose={() => { setReunionSemaine(null); loadEvents(); }} />
      )}
      <PlanifierReunionDialog
        chantierId={chantierId}
        open={planifier !== null}
        dateInitiale={planifier || ''}
        onClose={() => setPlanifier(null)}
        onCreated={() => loadEvents()}
      />
      {crPourReunion && (
        <CreateCRCDialog
          chantierId={chantierId}
          open={!!crPourReunion}
          reunionInitiale={crPourReunion}
          onClose={() => setCrPourReunion(null)}
          onCreated={(crc) => { setCrPourReunion(null); loadEvents(); onCrCree && onCrCree(crc); }}
        />
      )}

      {/* RP13 / D15 — calendrier des réunions (evenements) ; le jour choisi pré-remplit
          « Planifier une réunion ». */}
      {!loading && (
        <ProjectPlanning
          chantierId={chantierId}
          events={events}
          onDateChange={setJourChoisi}
          onSelect={setSelectedEvt}
        />
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <>
          {upcoming.length === 0 && past.length === 0 ? (
            <div className="card-kroquis text-center py-16">
              <CalendarBlank size={48} className="mx-auto text-[#E0E0DB] mb-4" />
              <p className="text-[#6B6B6B]">Aucune réunion planifiée</p>
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">À venir ({upcoming.length})</p>
                  <div className="space-y-2">
                    {upcoming.map(evt => {
                      const d = new Date(evt.date_debut);
                      const invites = evt.invites || [];
                      const confirmed = invites.filter(i => i.reponse === 'confirme').length;
                      const declined = invites.filter(i => i.reponse === 'decline' || i.reponse === 'excuse').length;
                      const pending = invites.length - confirmed - declined;
                      return (
                        <div key={evt.id} className="card-kroquis hover:border-[#2C2C2C] cursor-pointer transition-colors" onClick={() => setSelectedEvt(evt)} data-testid={`reunion-${evt.id}`}>
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="text-center min-w-[44px]">
                              <p className="text-xl font-bold text-[#C75B39]" style={{ fontFamily: 'DM Serif Display, serif' }}>{d.getDate()}</p>
                              <p className="text-[10px] text-[#6B6B6B] uppercase">{d.toLocaleDateString('fr-FR', { month: 'short' })}</p>
                            </div>
                            <div className="flex-1 min-w-[180px]">
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="font-medium text-[#2C2C2C] truncate">{evt.titre}</h4>
                                <EtatConvocation evt={evt} />
                              </div>
                              <p className="text-xs text-[#6B6B6B]">
                                {d.toLocaleDateString('fr-FR', { weekday: 'long' })} à {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} — {evt.lieu || 'Sur site'}
                              </p>
                              {invites.length > 0 && (
                                <p className="text-xs mt-1">
                                  <span className="text-[#2D6A4F]">{confirmed} présent{confirmed > 1 ? 's' : ''}</span>
                                  {pending > 0 && <span className="text-[#B8860B]"> · {pending} sans réponse</span>}
                                  {declined > 0 && <span className="text-[#8B3A3A]"> · {declined} absent{declined > 1 ? 's' : ''}</span>}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1" onClick={e => e.stopPropagation()}>
                              {canCreate && (
                                <Button variant="outline" size="sm" className="rounded-none text-xs gap-1 h-8" onClick={() => setReunionSemaine({ evenementId: evt.id })} data-testid={`ouvrir-reunion-${evt.id}`}>
                                  <Users size={12} /> Ouvrir la réunion
                                </Button>
                              )}
                              {!evt.convocation_envoyee && canCreate && (
                                <Button variant="outline" size="sm" className="rounded-none text-xs gap-1 h-8" onClick={() => sendConvocations(evt)} data-testid={`convoquer-${evt.id}`}>
                                  <PaperPlaneRight size={12} /> Convoquer
                                </Button>
                              )}
                              {evt.convocation_envoyee && canCreate && (
                                <>
                                  <Button variant="outline" size="sm" className="rounded-none text-xs gap-1 h-8" onClick={() => sendConvocations(evt, true)} data-testid={`renvoyer-${evt.id}`}>
                                    <PaperPlaneRight size={12} /> Renvoyer
                                  </Button>
                                  <Button variant="outline" size="sm" className="rounded-none text-xs gap-1 h-8" onClick={() => sendRappel(evt.id)} data-testid={`rappel-${evt.id}`}>
                                    <Clock size={12} /> Rappel
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Passées</p>
                  <div className="space-y-2">
                    {past.map(evt => (
                      <div key={evt.id} className="card-kroquis cursor-pointer" onClick={() => setSelectedEvt(evt)} data-testid={`reunion-passee-${evt.id}`}>
                        <div className="flex flex-wrap items-center gap-4">
                          <p className="text-sm text-[#6B6B6B]">{new Date(evt.date_debut).toLocaleDateString('fr-FR')}</p>
                          <p className="text-sm font-medium truncate flex-1">{evt.titre}</p>
                          {evt.compte_rendu_id ? (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#E3F2FD] text-[#1E3A5F] font-semibold">Compte rendu rédigé</span>
                          ) : canCreate && (
                            <Button size="sm" className="rounded-none text-xs gap-1 h-8 bg-[#C4773B] hover:bg-[#A5623A]" onClick={(e) => { e.stopPropagation(); setCrPourReunion(evt.id); }} data-testid={`rediger-cr-${evt.id}`}>
                              <ClipboardText size={12} /> Rédiger le compte rendu
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Detail panel */}
      {selectedEvt && (
        <Dialog open={!!selectedEvt} onOpenChange={() => setSelectedEvt(null)}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{selectedEvt.titre}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#6B6B6B]">
                {new Date(selectedEvt.date_debut).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
              {selectedEvt.lieu && <p className="text-sm text-[#6B6B6B]">Lieu : {selectedEvt.lieu}</p>}
              <EtatConvocation evt={selectedEvt} />
              {selectedEvt.recurrence && <p className="text-xs text-[#C75B39]">Récurrence : {selectedEvt.recurrence}</p>}
              {(selectedEvt.invites || []).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-[#6B6B6B] uppercase">Pointage de présence ({selectedEvt.invites.length})</p>
                    <span className="text-[10px] text-[#9A9A9A]">Cliquez pour modifier</span>
                  </div>
                  <div className="space-y-1" data-testid="pointage-presence-list">
                    {selectedEvt.invites.map((inv, i) => (
                      <PresenceRow
                        key={i}
                        invite={inv}
                        onSet={async (reponse) => {
                          try {
                            await axios.patch(
                              `${API_URL}/api/chantiers/${chantierId}/evenements/${selectedEvt.id}/presence`,
                              { invite_index: i, reponse },
                              { withCredentials: true }
                            );
                            const updated = { ...selectedEvt, invites: selectedEvt.invites.map((it, j) => j === i ? { ...it, reponse } : it) };
                            setSelectedEvt(updated);
                            loadEvents();
                          } catch { toast.error('Erreur pointage'); }
                        }}
                        canEdit={canCreate}
                      />
                    ))}
                  </div>
                </div>
              )}
              {(selectedEvt.ordre_du_jour || []).filter(o => o).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#6B6B6B] uppercase mb-2">Ordre du jour</p>
                  <ol className="list-decimal list-inside text-sm space-y-1">
                    {selectedEvt.ordre_du_jour.filter(o => o).map((item, i) => <li key={i}>{item}</li>)}
                  </ol>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {canCreate && (
                  <Button
                    className="bg-[#C75B39] hover:bg-[#D97B5C] rounded-none gap-1 flex-1"
                    onClick={() => { sendConvocations(selectedEvt, !!selectedEvt.convocation_envoyee); setSelectedEvt(null); }}
                  >
                    <PaperPlaneRight size={14} /> {selectedEvt.convocation_envoyee ? 'Renvoyer la convocation' : 'Envoyer la convocation'}
                  </Button>
                )}
                {canCreate && !selectedEvt.compte_rendu_id && new Date(selectedEvt.date_debut) < new Date() && (
                  <Button variant="outline" className="rounded-none gap-1" onClick={() => { setCrPourReunion(selectedEvt.id); setSelectedEvt(null); }}>
                    <ClipboardText size={14} /> Rédiger le compte rendu
                  </Button>
                )}
                {canCreate && (
                  <Button variant="outline" className="rounded-none text-[#8B3A3A] hover:bg-[#FFEBEE]" onClick={() => deleteEvent(selectedEvt.id)} aria-label="Supprimer la réunion">
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// === CRC CONTENT EDITOR ===
const STATUT_CRC_CONFIG = {
  brouillon: { label: CRC_STATUT_LABELS.brouillon, color: 'bg-[#FFF8E1] text-[#B8860B] border-[#B8860B]' },
  valide: { label: CRC_STATUT_LABELS.valide, color: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]' },
  publie: { label: CRC_STATUT_LABELS.publie, color: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' },
  diffuse: { label: CRC_STATUT_LABELS.diffuse, color: 'bg-[#2D6A4F] text-white border-[#2D6A4F]' },
  signe: { label: CRC_STATUT_LABELS.signe, color: 'bg-[#1E3A5F] text-white border-[#1E3A5F]' },
};

function CRCContentEditor({ crc, chantierId, canEdit, canDiffuser = false, onUpdated }) {
  const [editing, setEditing] = useState(false);
  // Priorité : rapport_ia (rédigé par IA, toujours le plus récent) > contenu_ia > pv_content
  const [content, setContent] = useState(crcTexteOfficiel(crc));
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  // CHA-11 — état canonique : un CRC diffusé (publié + e-mail, ou « ENVOYE ») est verrouillé
  const statut = crcStatut(crc);
  const cfg = STATUT_CRC_CONFIG[statut] || STATUT_CRC_CONFIG.brouillon;

  // Re-sync si le CRC est rafraîchi par le parent (après génération IA par exemple)
  useEffect(() => {
    if (!editing) {
      setContent(crcTexteOfficiel(crc));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crc.rapport_ia, crc.contenu_ia, crc.pv_content]);

  const patch = async (data, successMsg) => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}`, data, { withCredentials: true });
      toast.success(successMsg);
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // Sauvegarde : si l'IA a rédigé un rapport on persiste via rapport_ia, sinon contenu_ia classique
  const saveContent = () => patch(
    crc.rapport_ia ? { rapport_ia: content } : { contenu_ia: content },
    'Contenu sauvegardé',
  );

  const regenerateAI = async () => {
    const confirm = window.confirm(
      crc.rapport_ia
        ? 'Régénérer le rapport avec IA ? Le texte actuel sera remplacé (vous pourrez le ré-éditer ensuite).'
        : 'Générer le rapport avec IA ? Cela prend 10 à 30 secondes.',
    );
    if (!confirm) return;
    setGeneratingAI(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/generate-ai`,
        {},
        { withCredentials: true, timeout: 90000 },
      );
      const md = res.data.rapport_markdown || '';
      setContent(md);
      setEditing(true);
      const ctx = res.data.context_summary || {};
      toast.success(
        `Rapport rédigé par l'Assistant (${res.data.usage?.output_tokens || '?'} tokens · ${ctx.historique || 0} CR précédents, ${ctx.photos || 0} photos)`,
      );
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération IA');
    } finally {
      setGeneratingAI(false);
    }
  };
  // RP17 — le collaborateur soumet : statut « À relire » + e-mail à l'architecte.
  const soumettre = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/soumettre`, {}, { withCredentials: true });
      const noms = data?.relecteurs_prevenus || [];
      toast.success(noms.length ? `Compte rendu soumis à l'architecte (${noms.join(', ')})` : "Compte rendu soumis à l'architecte");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  const publish = () => patch({ statut: 'publie' }, 'Compte rendu diffusé en ligne — visible par les clients et entreprises');
  const backToDraft = () => patch({ statut: 'brouillon' }, canDiffuser ? 'Compte rendu renvoyé au rédacteur' : 'Retour au brouillon');

  const publishAndDiffuse = async () => {
    if (!window.confirm('Valider et diffuser ce compte rendu à tous les destinataires (équipe MOE + clients + entreprises) ? Un délai de 8 jours ouvrés sera accordé pour contester.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/diffuser`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Compte rendu diffusé');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur diffusion');
    } finally {
      setSaving(false);
    }
  };

  const downloadDocx = () => {
    window.open(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/export-docx`, '_blank');
  };

  const downloadPhotosPdf = () => {
    window.open(`${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/export-photos-pdf`, '_blank');
  };

  // Détection présence photos (photos_meta OU selection.photo_ids)
  const photoCount = (() => {
    const meta = Array.isArray(crc?.photos_meta) ? crc.photos_meta.length : 0;
    if (meta > 0) return meta;
    const sel = crc?.selection?.photo_ids;
    return Array.isArray(sel) ? sel.length : 0;
  })();

  return (
    <div className="card-kroquis" data-testid="crc-content-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Compte rendu</h3>
          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border ${cfg.color}`} data-testid="crc-statut-badge">{cfg.label}</span>
          {crc.source === 'claude-sonnet' && <span className="text-[10px] text-[#C4773B] flex items-center gap-1"><Robot size={10} /> IA</span>}
          {crc.rapport_ia && (
            <span
              className="text-[10px] bg-[#F6EFFB] text-[#7B4E9B] border border-[#7B4E9B]/30 px-1.5 py-0.5 flex items-center gap-1"
              title={`Rapport rédigé par l'Assistant${crc.rapport_ia_generated_at ? ' le ' + crc.rapport_ia_generated_at.slice(0, 10) : ''}`}
              data-testid="crc-ai-badge"
            >
              <Robot size={10} weight="fill" /> Rédigé par l'Assistant
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !editing && statut === 'brouillon' && (
            <>
              <Button size="sm" variant="outline" className="rounded-none gap-1 h-7 text-xs" onClick={() => setEditing(true)} data-testid="crc-edit-btn">
                <PencilSimple size={12} /> Modifier
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-none gap-1 h-7 text-xs border-[#7B4E9B] text-[#7B4E9B] hover:bg-[#F6EFFB]"
                onClick={regenerateAI}
                disabled={generatingAI}
                title="Rédiger le rapport avec l'Assistant (contexte du chantier et comptes rendus précédents inclus)"
                data-testid="crc-generate-ai-btn"
              >
                {generatingAI ? (
                  <ArrowClockwise size={12} className="animate-spin" />
                ) : (
                  <Robot size={12} weight="fill" />
                )}
                {crc.rapport_ia ? 'Régénérer (Assistant)' : "Rédiger avec l'Assistant"}
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" className="rounded-none gap-1 h-7 text-xs" onClick={downloadDocx} data-testid="crc-export-docx">
            <FilePdf size={12} /> {BOUTONS.telechargerWord}
          </Button>
          {photoCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-none gap-1 h-7 text-xs border-[#C4773B] text-[#C4773B] hover:bg-[#FDF6EC]"
              onClick={downloadPhotosPdf}
              title={`Annexe photographique — ${photoCount} photo(s)`}
              data-testid="crc-export-photos-pdf"
            >
              <Image size={12} /> Annexe photos ({photoCount})
            </Button>
          )}
          {/* RP17-19 / D14 — circuit de relecture :
              collaborateur : Brouillon → « Soumettre à l'architecte » → À relire ;
              architecte (droit « Peut diffuser un CR ») : « Valider et diffuser »
              ou « Renvoyer au rédacteur ». */}
          {canEdit && !canDiffuser && statut === 'brouillon' && (
            <Button size="sm" className="bg-[#1E3A5F] hover:bg-[#2C4F7D] rounded-none gap-1 h-7 text-xs" onClick={soumettre} disabled={saving} data-testid="crc-soumettre-btn">
              <CheckCircle size={12} weight="bold" /> {BOUTONS.soumettreArchitecte}
            </Button>
          )}
          {canEdit && !canDiffuser && statut === 'valide' && (
            <>
              <span className="text-[10px] text-[#1E3A5F]" data-testid="crc-en-relecture">En relecture chez l'architecte</span>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={backToDraft} disabled={saving} data-testid="crc-reprendre-btn">↶ Reprendre le brouillon</Button>
            </>
          )}
          {canEdit && canDiffuser && (statut === 'brouillon' || statut === 'valide') && (
            <>
              {statut === 'valide' && (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={backToDraft} disabled={saving} data-testid="crc-renvoyer-btn">↶ Renvoyer au rédacteur</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-none gap-1 h-7 text-xs" onClick={publish} disabled={saving} data-testid="crc-publish-btn">
                <PaperPlaneRight size={12} weight="bold" /> Diffuser en ligne (sans e-mail)
              </Button>
              <Button size="sm" className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-1 h-7 text-xs text-white" onClick={publishAndDiffuse} disabled={saving} data-testid="crc-publish-diffuse-btn">
                <PaperPlaneRight size={12} weight="bold" /> {BOUTONS.validerEtDiffuser}
              </Button>
            </>
          )}
          {canEdit && canDiffuser && statut === 'publie' && (
            <>
              <Button size="sm" className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-1 h-7 text-xs text-white" onClick={publishAndDiffuse} disabled={saving} data-testid="crc-diffuse-btn">
                <PaperPlaneRight size={12} weight="bold" /> Diffuser par e-mail
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={backToDraft} disabled={saving}>↶ Brouillon</Button>
            </>
          )}
          {(statut === 'diffuse' || statut === 'signe') && (
            <span className="text-[10px] text-[#6B6B6B]" title="Un compte rendu diffusé est opposable : les corrections se font dans le compte rendu suivant." data-testid="crc-verrou">
              🔒 Diffusé — contenu verrouillé
            </span>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2" data-testid="crc-editor-2col">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Éditeur Markdown</p>
              <Textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={30}
                className="rounded-none font-mono text-xs leading-relaxed"
                data-testid="crc-editor-textarea"
              />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Prévisualisation</p>
              <div
                className="border border-[#E0E0DB] p-4 overflow-y-auto bg-white"
                style={{ height: 720 }}
                data-testid="crc-editor-preview"
              >
                <CRCMarkdownRenderer md={content} photosMeta={crc.photos_meta} sourcesIndex={crc.sources_index} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setContent(crcTexteOfficiel(crc)); setEditing(false); }}>Annuler</Button>
            <Button size="sm" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" onClick={async () => { await saveContent(); setEditing(false); }} disabled={saving} data-testid="crc-save-content">
              {saving ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="prose prose-sm max-w-none text-[#2C2C2C] crc-markdown" data-testid="crc-content-rendered">
          <CRCMarkdownRenderer md={content} photosMeta={crc.photos_meta} sourcesIndex={crc.sources_index} />
        </div>
      )}

      {/* Suivi de diffusion */}
      {crc.diffusion_date && (
        <CRCDiffusionTracker chantierId={chantierId} crcId={crc.id} />
      )}
    </div>
  );
}

function CRCDiffusionTracker({ chantierId, crcId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/diffusion`, { withCredentials: true });
        setData(res.data);
      } catch { /* ignore */ }
    })();
  }, [chantierId, crcId]);
  if (!data) return null;

  const statuts = {
    envoye: { label: 'Envoyé', color: 'text-[#6B6B6B]' },
    lu: { label: 'Lu', color: 'text-[#1E3A5F]' },
    valide_tacite: { label: 'Validé tacite', color: 'text-[#2D6A4F]' },
    conteste: { label: 'Contesté', color: 'text-[#8B3A3A]' },
    approuve: { label: 'Accepté', color: 'text-[#2D6A4F]' }, // RP23 — « J'approuve » sur la page publique
    en_attente: { label: 'En attente', color: 'text-[#6B6B6B]' },
  };

  const now = new Date();
  const limite = data.date_limite ? new Date(data.date_limite) : null;

  return (
    <div className="mt-5 pt-5 border-t border-[#E0E0DB]" data-testid="crc-diffusion-tracker">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">
          Suivi de diffusion ({(data.destinataires || []).length})
        </h4>
        {limite && (
          <span className="text-xs text-[#6B6B6B]">
            Délai de contestation : jusqu'au <strong>{limite.toLocaleDateString('fr-FR')}</strong>
            {limite < now && <span className="ml-2 text-[#2D6A4F]">(dépassé → validation tacite)</span>}
          </span>
        )}
      </div>
      <table className="w-full text-xs" data-testid="diffusion-table">
        <thead className="bg-[#F5F5F3]">
          <tr className="text-left uppercase text-[#6B6B6B]">
            <th className="p-2">Destinataire</th>
            <th className="p-2">Rôle</th>
            <th className="p-2">Envoyé</th>
            <th className="p-2">Consulté</th>
            <th className="p-2">Statut</th>
          </tr>
        </thead>
        <tbody>
          {(data.destinataires || []).map((d, idx) => {
            const st = statuts[d.statut] || statuts.en_attente;
            return (
              <tr key={idx} className="border-t border-[#F0F0EB]">
                <td className="p-2">
                  <div className="font-medium text-[#2C2C2C]">{d.nom}</div>
                  <div className="text-[10px] text-[#6B6B6B]">{d.email}</div>
                </td>
                <td className="p-2 text-[#6B6B6B]">{d.role || '—'}</td>
                <td className="p-2 text-[#6B6B6B]">{d.envoye_le ? new Date(d.envoye_le).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="p-2 text-[#6B6B6B]">{d.lien_clique_le ? new Date(d.lien_clique_le).toLocaleDateString('fr-FR') : '—'}</td>
                <td className={`p-2 font-medium ${st.color}`}>
                  {st.label}
                  {d.contestation?.motif && <div className="text-[10px] italic text-[#8B3A3A]">« {d.contestation.motif.slice(0, 80)} »</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// (Ancien MarkdownRenderer remplacé par CRCMarkdownRenderer — react-markdown + remark-gfm)

// === PRESENCE ROW — Pointage de présence ===
function PresenceRow({ invite, onSet, canEdit }) {
  const rep = invite.reponse || 'en_attente';
  const cfg = {
    confirme: { label: 'Présent', color: 'text-[#2D6A4F]', bg: 'bg-[#2D6A4F]' },
    decline: { label: 'Absent', color: 'text-[#8B3A3A]', bg: 'bg-[#8B3A3A]' },
    excuse: { label: 'Excusé', color: 'text-[#B8860B]', bg: 'bg-[#B8860B]' },
    en_attente: { label: 'Non confirmé', color: 'text-[#9A9A9A]', bg: 'bg-[#9A9A9A]' },
  }[rep] || { label: 'Non confirmé', color: 'text-[#9A9A9A]', bg: 'bg-[#9A9A9A]' };
  const testKey = invite.email || invite.nom || '';

  return (
    <div className="flex items-center gap-2 text-sm py-1.5 border-b border-[#E0E0DB] last:border-0" data-testid={`presence-row-${testKey}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.bg}`} />
      <div className="flex-1 min-w-0">
        <span className="font-medium">{invite.nom}</span>
        {invite.role && <span className="text-xs text-[#9A9A9A] ml-2">{invite.role}</span>}
      </div>
      {canEdit ? (
        <div className="flex gap-0.5">
          <button
            onClick={() => onSet('confirme')}
            className={`w-7 h-7 border flex items-center justify-center text-xs transition-colors ${rep === 'confirme' ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]' : 'text-[#2D6A4F] border-[#E0E0DB] hover:border-[#2D6A4F]'}`}
            title="Présent"
            data-testid={`presence-confirme-${testKey}`}
          >✓</button>
          <button
            onClick={() => onSet('decline')}
            className={`w-7 h-7 border flex items-center justify-center text-xs transition-colors ${rep === 'decline' ? 'bg-[#8B3A3A] text-white border-[#8B3A3A]' : 'text-[#8B3A3A] border-[#E0E0DB] hover:border-[#8B3A3A]'}`}
            title="Absent"
            data-testid={`presence-decline-${testKey}`}
          >✕</button>
          <button
            onClick={() => onSet('excuse')}
            className={`w-7 h-7 border flex items-center justify-center text-xs transition-colors ${rep === 'excuse' ? 'bg-[#B8860B] text-white border-[#B8860B]' : 'text-[#B8860B] border-[#E0E0DB] hover:border-[#B8860B]'}`}
            title="Excusé"
            data-testid={`presence-excuse-${testKey}`}
          >!</button>
        </div>
      ) : (
        <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
      )}
    </div>
  );
}
